<script lang="ts">
  /**
   * The moderation queue as ONE THING AT A TIME, always shown inside its post.
   *
   * WHY THIS REPLACED A LIST. The list asked the reader to hold too much: posts and
   * comments interleaved as peers, each row a different kind of object, a reply that
   * opened somewhere else, and filters above it all. People know posts. They do not
   * think in "inbox items", and a flagged comment shown as a row of its own — stripped
   * of the conversation it belongs to — is not something anyone can answer well.
   *
   * SO THE CARD IS THE POST, and the thread underneath it is the whole thread. What
   * MOVES is the focus: the reply box sits on the first thing in this post that nobody
   * answered, and answering it — or marking it read — walks the focus to the next
   * thing IN THE SAME POST. The card only changes when the post is finished. Someone
   * working a post with five unanswered comments reads it once and answers five times,
   * instead of meeting the same post five times with no memory of the last visit.
   *
   * THERE IS NO GROUPING OR SORTING HERE ANY MORE, and that is the point of #171.
   * `…/moderation/queue` returns one row per POST, newest post first, each carrying the
   * comments still waiting on it in reading order. Both jobs used to be done locally
   * against one page at a time, and neither can be: grouping a page only groups what
   * that page holds, so a post whose flagged comments straddled a boundary became two
   * cards; and sorting a page only sorts what has been fetched, so an older unanswered
   * post on page 4 never outranked a newer one on page 1. This file now walks what it
   * is given.
   *
   * TWO CONTROLS, and Back really does undo. `PATCH …/moderation/items/{kind}/{id}`
   * takes `cleared` as a boolean, so marking read and undoing are the same call with a
   * different value — Back is not a local illusion that a refresh would expose.
   *
   * HOW FAR BACK IS A CHOICE — 24 hours, 3 days, 7 days, or everything. It used to be
   * a ladder that widened itself as each rung ran out, which reads as the queue
   * refilling: the count on screen grows while you are answering, and nothing says the
   * window moved. A window that only changes when it is asked to is a window you can
   * trust, and the reader is the one deciding how much to take on today.
   *
   * THE SAME BOUND DOES BOTH JOBS. It bounds the page, and it is the one `counts` entry
   * asked for — so the progress bar's denominator is the window on screen, and the bar
   * measures the thing that was picked rather than some wider backlog behind it.
   */
  import PostThread, { type ThreadTarget } from './PostThread.svelte';
  import StateBlock from './StateBlock.svelte';
  import { clearInboxItem, fetchQueue, isCodedError } from '@api';
  import type { Addressing, CommentReason, PostDetail, QueuePage, QueuePost } from '@api';

  /**
   * One thing to answer: a post nobody replied to, or a comment in one.
   *
   * `addressing` is what the Helper needs to post straight to Skool rather than through
   * the outbox. It is per ROW, not per post — a comment's reply is filed under the
   * hoisted top-level ancestor, which differs from the post's own trio. Null where the
   * row cannot be addressed at all, and then the panel queues, which is what it has
   * always done for an unresolvable row.
   */
  export interface QueueRow {
    postId: number;
    kind: 'post' | 'comment';
    id: number;
    /** `post` for the post itself; the comment's own flag otherwise. */
    reason: CommentReason | 'post';
    url: string | null;
    addressing: Addressing;
  }

  let {
    skoolId,
    compact = false,
    badges = true,
    now = Date.now(),
    composer,
    onLike = null,
    likedIds = null,
    deletedIds = null,
    transform,
    errorText,
    onChanged,
    onReady,
    seed = null,
    emptyHint,
    stickyTop = null,
  }: {
    /** Falsy means "no community yet" — `null` on the dashboard, `0` in the panel,
     *  which held one community id short of a real one and fetched against it. */
    skoolId: number | null;
    /** The 340px panel's density — same meaning as on `<SkoolPost>`. */
    compact?: boolean;
    /**
     * Ask the server to recompute the sidebar counters on every clear.
     *
     * Default true, because the dashboard renders them and not re-reading them after a
     * write is the reason they ride along. A host with its own counters should pass
     * `false` and pay nothing for them — see `clearOpts`. `onChanged` then carries
     * `badges: null`.
     */
    badges?: boolean;
    now?: number;
    /** The reply box, forwarded to `<PostThread>` — it receives the thread and the
     *  row being answered, so a host can send without this knowing how. */
    composer?: import('svelte').Snippet<[PostDetail, ThreadTarget]> | undefined;
    /**
     * Perform a like, if this surface can.
     *
     * Forwarded to `<PostThread>` untouched — the host writes to Skool, this only decides
     * what a like MEANS to the queue, which is `liked()` below.
     */
    onLike?:
      | ((target: { kind: 'post' | 'comment'; id: number; externalId: string }) => Promise<void> | void)
      | undefined
      | null;
    /** Skool ids the owner has already liked — forwarded to `<PostThread>`. Only a
     *  surface holding the owner's Skool session can supply this. */
    likedIds?: Set<string> | null;
    /** Skool ids that no longer exist upstream — forwarded the same way, and known by
     *  the same one surface. The rows still render and still clear; they are just
     *  marked, because a deleted row is the reason a queue row exists. */
    deletedIds?: Set<string> | null;
    /** A host's chance to fold in what only it knows, before the thread renders. */
    transform?: ((detail: PostDetail) => Promise<PostDetail> | PostDetail) | undefined;
    errorText?: ((err: unknown) => string | null) | undefined;
    /** A row was cleared or restored. Carries the recomputed sidebar counters so a host
     *  with badges never re-reads them, and the row, because the panel's Posts tab
     *  counts OPEN POSTS and has to know which kind moved. */
    onChanged?:
      | ((change: {
          badges: { inbox: number; outbox: number } | null;
          row: QueueRow;
          cleared: boolean;
        }) => void)
      | undefined;
    /**
     * What the page said about the community, on every load.
     *
     * The gate that decides whether this screen means anything at all. It rides along on
     * the page (#171) instead of costing a second request for a `stats` block priced by
     * the whole backlog.
     */
    onReady?: ((info: { hasModerators: boolean }) => void) | undefined;
    /**
     * A first page the host already has, used INSTEAD of this component's own.
     *
     * The dashboard asks for the account and the first page in one round trip (doc 04
     * §4.1) — the queue request goes out beside the bootstrap rather than after it.
     * Fetching again here would put that round trip back. Honoured only for the bound
     * it was fetched with, and only before anything has been walked.
     */
    seed?: { skoolId: number; bound: string; page: QueuePage } | null | undefined;
    /** Extra line under "All caught up" — the panel points at the web dashboard. */
    emptyHint?: import('svelte').Snippet | undefined;
    /**
     * Pin the window/progress card to this offset inside the host's scroll container.
     *
     * Presentation, like `compact`, and for the same host: the panel is one screen with
     * one scroller, and controls that scroll away in it are controls you cannot reach
     * while reading. The DASHBOARD scrolls the whole page behind its own chrome, so a
     * card pinned to the viewport top would sit over that chrome — it passes nothing
     * and the card scrolls with the thread, which is right there.
     *
     * A CSS length, because the offset is whatever the host has stacked above it (the
     * panel measures its community picker). Absent means not pinned at all.
     */
    stickyTop?: string | null;
  } = $props();

  const LIMIT = 25;

  /**
   * THE FOUR WINDOWS, named in the client because that is where pacing belongs.
   *
   * The route takes an arbitrary `?last=` and knows nothing about which bounds a reader
   * is offered — deliberately, so changing the ladder is not a backend deploy. These
   * four are the ladder's old rungs made pickable: the last day is finishable, and
   * `all` is the backlog nobody finishes, which is why it is a choice and not a default.
   */
  const WINDOWS = ['1d', '3d', '7d', 'all'] as const;
  type Bound = (typeof WINDOWS)[number];

  /**
   * The pill, in two lengths, and the same window said inside a sentence.
   *
   * The short set is not a cosmetic choice: on one row in a 340px panel the long
   * labels leave the bar nothing to occupy, and a `flex-1` with `min-w-0` shrinks to
   * zero rather than wrapping — so the progress silently disappeared instead of
   * moving. Two characters back per pill is what pays for it.
   */
  const LABEL: Record<Bound, string> = { '1d': '24h', '3d': '3 days', '7d': '7 days', all: 'All' };
  const SHORT: Record<Bound, string> = { '1d': '24h', '3d': '3d', '7d': '7d', all: 'All' };
  const NAMED: Record<Bound, string> = {
    '1d': 'the last 24 hours',
    '3d': 'the last 3 days',
    '7d': 'the last 7 days',
    all: 'all time',
  };

  let bound = $state<Bound>('1d');

  /**
   * What to ask the clear for besides the clear itself.
   *
   * `PATCH …/items/{kind}/{id}` recomputes the sidebar badge on every write, and that
   * badge is `domain.moderation.metrics` — six counts over a scope holding the
   * correlated EXISTS, measured at 20.5s on the largest community (#170). Two levers,
   * and a host needs the right one:
   *
   *   DECLINE IT (`badges: false`, #173) when nothing renders it. The Helper panel keeps
   *   its own per-tab, account-wide counters, so it threw this away on arrival and every
   *   Skip paid for a number nobody saw: 3.59s against 0.07s on the same row.
   *
   *   BOUND IT when something does. The sidebar counts the viewer's local day, so that
   *   is the window to send — omit it and the badge is computed over the route's 90-day
   *   default, which is both slower and not the number on screen.
   */
  function clearOpts(): { since?: string; badges?: boolean } {
    if (!badges) return { badges: false };
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return { since: midnight.toISOString() };
  }

  let posts = $state<QueuePost[]>([]);
  /** Position in the flattened list of things to answer. */
  let pos = $state(0);
  let nextCursor = $state<string | null>(null);
  /** The window's size when it opened. The live count shrinks as rows are cleared, so a
   *  bar measured against it would never fill. */
  let windowTotal = $state<number | null>(null);
  let loading = $state(false);
  /**
   * A page for THIS window has actually come back.
   *
   * Without it "exhausted" is true before anything is fetched — no cursor, no rows,
   * `pos` at 0 — so an empty state is shown before the first request has left. Set only
   * on success, so a failed load shows its error instead of being read as finished.
   */
  let settled = $state(false);
  let loadingMore = $state(false);
  let error = $state<string | null>(null);
  let thread = $state<ReturnType<typeof PostThread> | null>(null);

  /** What each answered row did, so Back knows what to take back. A reply cannot be
   *  unsent, so stepping back over one restores the focus and nothing else. */
  let history = $state<Map<string, 'cleared' | 'replied'>>(new Map());

  const key = (r: { kind: string; id: number }): string => `${r.kind}:${r.id}`;

  /**
   * Every row to answer, in the order the server gave them: posts newest-first, and
   * within a post its own row before its comments oldest-first.
   *
   * No sorting. `waiting[]` arrives in reading order and the pages arrive in queue
   * order, which is the whole reason this component stopped doing either.
   */
  const rows = $derived<QueueRow[]>(
    posts.flatMap((p) => {
      const own: QueueRow[] = p.postNeedsReply
        ? [
            {
              postId: p.postId,
              kind: 'post',
              id: p.postId,
              reason: 'post',
              url: p.url,
              addressing: p.addressing,
            },
          ]
        : [];
      const waiting = p.waiting.map(
        (w): QueueRow => ({
          postId: p.postId,
          kind: 'comment',
          id: w.commentId,
          reason: w.reason,
          url: p.url,
          // The comment's OWN trio, not the post's: the parent is hoisted per row.
          addressing: w.addressing,
        }),
      );
      return [...own, ...waiting];
    }),
  );

  const current = $derived<QueueRow | null>(rows[pos] ?? null);
  const remaining = $derived(Math.max(0, rows.length - pos));
  /** This window is walked out: a page came back, the cursor is spent, and there is
   *  nothing left after it. */
  const exhausted = $derived(settled && !loading && !nextCursor && pos >= rows.length);

  const target = $derived<ThreadTarget | null>(
    current ? { kind: current.kind, id: current.id, postId: current.postId } : null,
  );

  /** The comments on this post still waiting, minus anything answered in this session.
   *  The thread folds everything else away — see `<CommentThread>`. */
  const waitingIds = $derived(
    (posts.find((p) => p.postId === current?.postId)?.waiting ?? [])
      .filter((w) => !history.has(`comment:${w.commentId}`))
      .map((w) => w.commentId),
  );

  /**
   * Posts FINISHED in this window — nothing of them left to answer — in the same unit
   * `counts` answers in.
   *
   * NOT "posts we have passed a row of", which is what this counted. A post contributes
   * one row for itself and one per waiting comment, so skipping the post itself marked
   * the whole post done while the deck was still asking about its three comments. That
   * was survivable as a ratio and is not survivable as "All 1 answered" printed over a
   * card that is still waiting on a reply.
   */
  const postsDone = $derived.by(() => {
    const left = new Set(rows.slice(pos).map((r) => r.postId));
    return new Set(
      rows
        .slice(0, pos)
        .map((r) => r.postId)
        .filter((id) => !left.has(id)),
    ).size;
  });

  /**
   * How far through the post in focus, 0–1 — so the tile it sits on fills as you go.
   *
   * THE DENOMINATOR IS POSTS AND THE WORK IS ROWS, and one post can be a lot of rows:
   * measured one on prod with seven waiting comments and no post row of its own. Whole
   * tiles alone, that window is a single tile that sits empty through seven answers and
   * then jumps to full — a bar that does not move while you work is the one that gets
   * ignored. Counting rows instead is not available: `counts` counts posts, and the row
   * count for a window is only known for the pages fetched so far.
   */
  const postFraction = $derived.by(() => {
    const id = current?.postId;
    if (id == null) return 0;
    const total = rows.filter((r) => r.postId === id).length;
    if (!total) return 0;
    return rows.slice(0, pos).filter((r) => r.postId === id).length / total;
  });

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  function adopt(page: QueuePage, append: boolean): void {
    posts = append ? [...posts, ...page.items] : page.items;
    nextCursor = page.nextCursor;
    onReady?.({ hasModerators: page.hasModerators });
  }

  /** What `posts` currently describes, so the effect can tell a real change from its
   *  own re-run and the seed is consumed exactly once. */
  let loadedFor = $state<string | null>(null);

  async function load(): Promise<void> {
    if (!skoolId) return;
    loading = true;
    error = null;
    settled = false;
    posts = [];
    pos = 0;
    history = new Map();
    try {
      // ONE COUNT, THE ONE ON SCREEN. Every bound used to be counted on every load so
      // the ladder could skip an empty rung without asking. Nothing widens itself now,
      // so the other three are numbers nobody reads — and they were never free:
      // `queue_counts` bounds its scan by the WIDEST bound requested, so asking for
      // `7d` alongside `1d` made every load on the day window pay for a week-wide scan.
      const page = await fetchQueue(skoolId, { last: bound }, { limit: LIMIT, counts: [bound] });
      adopt(page, false);
      windowTotal = page.counts?.[bound] ?? null;
      settled = true;
    } catch (err: unknown) {
      error = errorText?.(err) ?? (err instanceof Error ? err.message : 'Failed to load the queue.');
    } finally {
      loading = false;
    }
  }

  /** Re-read from the top of the current rung. Exported: a host that sends replies
   *  elsewhere — the panel drains its outbox — asks once the send has landed. */
  export async function reload(): Promise<void> {
    await load();
  }

  async function loadMore(): Promise<void> {
    if (!skoolId || !nextCursor || loadingMore) return;
    loadingMore = true;
    try {
      const page = await fetchQueue(skoolId, { last: bound }, { limit: LIMIT, cursor: nextCursor });
      adopt(page, true);
    } catch (err: unknown) {
      // A relative bound resolves against the top of the hour and the cursor's filter
      // hash covers the resolved instant, so a walk that crosses an hour answers this.
      // Starting the rung again is the honest repair — the alternative the backend
      // rejected was serving a stale bound the client could not see. Anything else is
      // soft: the reader still has every row already loaded.
      if (isCodedError(err) && err.code === 'cursor_filter_mismatch') {
        nextCursor = null;
        await load();
      }
    } finally {
      loadingMore = false;
    }
  }

  // Keep enough ahead of the reader that advancing never waits on a request.
  $effect(() => {
    if (remaining <= 5 && nextCursor && !loading && !loadingMore) void loadMore();
  });

  // Reload when the community or the window changes, and only then.
  $effect(() => {
    const view = `${skoolId}|${bound}`;
    if (!skoolId || loadedFor === view) return;
    loadedFor = view;
    if (seed && seed.skoolId === skoolId && seed.bound === bound && pos === 0) {
      posts = [];
      history = new Map();
      adopt(seed.page, false);
      windowTotal = seed.page.counts?.[bound] ?? null;
      settled = true;
      return;
    }
    settled = false;
    void load();
  });

  // ---------------------------------------------------------------------------
  // The two controls
  // ---------------------------------------------------------------------------
  /**
   * WRITES DO NOT BLOCK THE INTERACTION.
   *
   * Skip used to await the PATCH before moving, so every press cost a round trip that
   * recomputes the sidebar badge — a metrics query, not a row update. Even bounded to a
   * day that is the wrong thing to make somebody wait for: they are waiting for the next
   * card, and the badge is a number in a sidebar.
   *
   * So the position moves at once and the write goes behind it, which is the same
   * paint-then-persist order the DM and notification marks use. Reverting the POSITION
   * on a failure would be wrong — by then the reader has moved on, and yanking them
   * backwards is worse than the row reappearing. Instead the row is dropped from
   * `history`, so it is no longer treated as handled and the next `reload()` shows it
   * again, which is what the server still believes too.
   *
   * SERIALISED, because rapid skipping would otherwise fan out one expensive write per
   * press. They go out one at a time, in order, and the reader never waits for any of
   * them.
   */
  let chain: Promise<void> = Promise.resolve();

  function persist(work: () => Promise<void>): void {
    chain = chain.then(work).catch(() => {
      /* `work` reports its own failure; this only keeps the chain alive */
    });
  }

  export function markRead(): void {
    const r = current;
    const sid = skoolId;
    if (!r || !sid) return;
    skipLiked = true; // moving forward again — re-arm the skip

    history.set(key(r), 'cleared');
    history = new Map(history);
    pos += 1;
    error = null;
    persist(async () => {
      try {
        const res = await clearInboxItem(sid, r.kind, r.id, true, clearOpts());
        onChanged?.({ badges: res.badges, row: r, cleared: true });
      } catch (err: unknown) {
        history.delete(key(r));
        history = new Map(history);
        error =
          errorText?.(err) ?? (err instanceof Error ? err.message : 'Could not mark that read.');
      }
    });
  }

  /**
   * "This whole post is dealt with" — clear its own row and every comment still
   * waiting on it, in one press.
   *
   * WHY THIS IS NOT A NEW CONCEPT. It looks like it needs a "completed" flag on the
   * post; it does not, and adding one would have been a second definition of a
   * question `queue_scope` already answers. A post is queued when it is unanswered
   * itself OR holds a waiting comment, and both arms are per-row `mod_cleared_at`
   * — so clearing every open row under it is exactly what "done" means, expressed in
   * the vocabulary the queue already has. `markRead()` is this for one row; this is
   * the same write, N times.
   *
   * IT COMES BACK BY ITSELF, which is the point and costs nothing. A comment posted
   * after this press has `mod_cleared_at` null, so the waiting arm goes true again and
   * the post re-enters the queue on the next read. Nothing has to remember that it was
   * ever completed — a flag would have had to be cleared by that new comment, and
   * forgetting to is how a post goes permanently invisible.
   *
   * N+1 REQUESTS, KNOWINGLY. Clearing is per item and the bulk route filters by
   * kind/reason/window rather than by post, so this is one PATCH per open row. That is
   * a deliberate trade for shipping without a backend change, and it is why the
   * failure path below reports a PARTIAL result instead of claiming the press worked:
   * with no transaction, some can land and some can fail, and a button called
   * "Done with Post" that silently did four of seven would be worse than one that says
   * so. The rows that failed are dropped from `history`, so the next `reload()` shows
   * them again — which is what the server still believes.
   */
  export function completePost(): void {
    const r = current;
    const sid = skoolId;
    if (!r || !sid) return;
    const postId = r.postId;
    // From `rows`, not from `waitingIds`: this must include the post's OWN row when it
    // has one, and `rows` is the same unit the deck walks — so "cleared everything"
    // and "the deck has nothing left here" cannot disagree.
    const mine = rows.filter((x) => x.postId === postId && !history.has(key(x)));
    if (!mine.length) return;

    for (const x of mine) history.set(key(x), 'cleared');
    history = new Map(history);
    // Step over every remaining row of this post, wherever the reader was inside it.
    // They are contiguous — `rows` is built post by post.
    let next = pos;
    while (next < rows.length && rows[next]?.postId === postId) next += 1;
    pos = next;
    error = null;

    persist(async () => {
      const failed: QueueRow[] = [];
      for (const x of mine) {
        try {
          const res = await clearInboxItem(sid, x.kind, x.id, true, clearOpts());
          onChanged?.({ badges: res.badges, row: x, cleared: true });
        } catch {
          failed.push(x);
        }
      }
      if (!failed.length) return;
      for (const x of failed) history.delete(key(x));
      history = new Map(history);
      const done = mine.length - failed.length;
      error =
        `Marked ${done} of ${mine.length} as answered — ${failed.length} did not go through. ` +
        `They will be back the next time this list loads.`;
    });
  }

  /** How many rows one press of "Done with Post" would clear. Zero means the reader has
   *  already handled everything here by hand, and the control has nothing to do. */
  const completable = $derived(
    current ? rows.filter((x) => x.postId === current.postId && !history.has(key(x))).length : 0,
  );

  /**
   * WHAT those rows are, which is not derivable from the count.
   *
   * The post's own row is present only when the post itself is unanswered
   * (`postNeedsReply`), and a post can be queued purely on its comments — most are.
   * So a count of 3 is three COMMENTS on a post that needs no reply itself just as
   * often as it is a post plus two. The first draft of this tooltip assumed the post
   * was always one of them and told a reader looking at three waiting comments that it
   * would "mark this post and its 2 waiting comments" — wrong about both halves.
   */
  const completableParts = $derived.by(() => {
    const r = current;
    if (!r) return { post: false, comments: 0 };
    const mine = rows.filter((x) => x.postId === r.postId && !history.has(key(x)));
    return {
      post: mine.some((x) => x.kind === 'post'),
      comments: mine.filter((x) => x.kind === 'comment').length,
    };
  });

  /**
   * Where the CONTENT COLUMN ends, so the pill can sit against it rather than against
   * the window.
   *
   * This root is `mx-auto max-w-3xl`, so on a wide dashboard the column is centred and
   * the viewport's right edge is empty page beside it — a `right-5` pill floated out
   * there in the gutter, detached from the card it acts on. In the side panel the two
   * edges coincide, which is why it looked correct there and only there.
   *
   * Measured rather than guessed because the offset is not a constant: it depends on
   * the window width, the host's own chrome, and whether a scrollbar is taking space.
   */
  /**
   * How far inside the column's edges the floating pills sit.
   *
   * Flush with the edge is where measuring put them, and it read as a mistake: both
   * pills touched the card's border exactly, so they looked stuck to it rather than
   * floating over it. One number for both sides, applied to the same measurement, so
   * they cannot drift apart the way they did when each measured its own box.
   */
  const FLOAT_INSET = 16;

  let rootEl = $state<HTMLElement | null>(null);
  let anchorLeft = $state(20);
  let anchorRight = $state(20);

  $effect(() => {
    const el = rootEl;
    if (!el) return;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      // BOTH EDGES OF THE SAME BOX, which is the only way the pair looks deliberate.
      // They were measured against different elements once — this column for the right
      // one, the composer's own rect for the left — and the composer sits INSIDE the
      // card, so it carries the card's padding and the left pill sat visibly further
      // in than the right pill sat out. Same box, same inset, symmetric result.
      anchorLeft = Math.max(8, Math.round(r.left) + FLOAT_INSET);
      anchorRight = Math.max(8, Math.round(window.innerWidth - r.right) + FLOAT_INSET);
    };
    measure();
    // Same three signals the composer's own floating button watches, and for the same
    // reason: scroll and resize miss a column that changes width because something
    // above it grew. See `ReplyComposer`'s note on the observer being a change
    // detector only — `measure()` stays the single definition of the offset.
    window.addEventListener('scroll', measure, { passive: true, capture: true });
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  });

  const completeTitle = $derived.by(() => {
    const { post, comments } = completableParts;
    const c = `${comments} waiting ${comments === 1 ? 'comment' : 'comments'}`;
    if (post && comments) return `Mark this post and its ${c} as answered`;
    if (post) return 'Mark this post as answered';
    if (comments) return `Mark the ${c} on this post as answered`;
    return 'Nothing left to mark here';
  });

  /** The same number, for a host that wants to label its own control. */
  export function completableCount(): number {
    return completable;
  }

  export function back(): void {
    const sid = skoolId;
    if (pos === 0 || !sid) return;
    // Deliberately going backwards. Auto-skip would undo the step before it landed.
    skipLiked = false;
    const r = rows[pos - 1];
    if (!r) return;
    const was = history.get(key(r));
    history.delete(key(r));
    history = new Map(history);
    pos -= 1;
    error = null;
    // Only a mark-read is undone on the server. A reply is out; putting the row back
    // would claim otherwise, so stepping over one only returns the focus.
    if (was !== 'cleared') return;
    persist(async () => {
      try {
        const res = await clearInboxItem(sid, r.kind, r.id, false, clearOpts());
        onChanged?.({ badges: res.badges, row: r, cleared: false });
      } catch (err: unknown) {
        error = errorText?.(err) ?? (err instanceof Error ? err.message : 'Could not undo that.');
      }
    });
  }

  /** True once something has been answered and there is a step to take back. */
  export function canUndo(): boolean {
    return pos > 0;
  }

  /**
   * The row in focus, for a host that needs more than the target.
   *
   * The panel reads `addressing` off it to post straight to Skool rather than queueing.
   * It arrives per row (#171): a comment's reply is filed under the hoisted top-level
   * ancestor, so its trio is not the post's. All three null together means the row
   * cannot be addressed and the panel queues, which is correct rather than a failure.
   */
  export function currentRow(): QueueRow | null {
    return current;
  }

  /** Re-read the thread on the card, without re-reading the queue. The panel repaints
   *  this way when the BACKEND has moved. */
  export async function reloadThread(): Promise<void> {
    await thread?.reload();
  }

  /** Re-apply the host's transform to the thread already on the card. No network.
   *  The panel repaints this way when a staged reply — or anything else this browser
   *  read from Skool before the backend saw it — lands in storage. */
  export async function restageThread(): Promise<void> {
    await thread?.restage();
  }

  /**
   * A reply landed. Advances only when it answered the row in focus — the reader can
   * move the box to any other comment in the thread, and a reply aimed there is not an
   * answer to what the deck is asking about.
   */
  export function replied(to: { kind: 'post' | 'comment'; id: number }): void {
    const r = current;
    if (!r || key(r) !== key(to)) return;
    history.set(key(r), 'replied');
    history = new Map(history);
    pos += 1;
  }

  /**
   * The host's like, with its failure put where the reader can see it.
   *
   * `<Upvote>` reverts its own paint on a throw, which stops the UI claiming something
   * that did not happen — but a thumb that fills and then quietly empties is exactly the
   * "I clicked like and nothing happened" this shipped with. The deck already owns the one
   * error line this surface has, and a like is the deck's business now that it clears
   * rows, so the message goes there.
   *
   * Re-thrown after recording, because the revert is `<Upvote>`'s job and it needs the
   * rejection to do it.
   */
  const reportedLike = $derived(
    onLike
      ? async (t: { kind: 'post' | 'comment'; id: number; externalId: string }) => {
          try {
            error = null;
            await onLike(t);
          } catch (e: unknown) {
            error =
              errorText?.(e) ?? (e instanceof Error ? e.message : 'Could not like that.');
            throw e;
          }
        }
      : null,
  );

  /**
   * A like landed, and a like is an answer.
   *
   * The product rule: liking something IS marking it dealt with — that is the whole
   * reason the control is here rather than as a courtesy. So this is `markRead()`, not a
   * second write path: the row clears on the server and the deck moves on exactly as Skip
   * does, and the undo is the same Back.
   *
   * Same guard as `replied()`. The reader can like any comment in the thread, and one
   * aimed at a row the deck is NOT asking about is just a like — there is nothing in the
   * queue to clear and nothing to advance past.
   */
  export function liked(to: { kind: 'post' | 'comment'; id: number }): void {
    const r = current;
    if (!r || key(r) !== key(to)) return;
    markRead();
  }

  /**
   * SKOOL IDS FOR THE ROWS ON THIS CARD, learned when the thread lands.
   *
   * The queue row has no `external_id` — `QueuePost`/`QueueWaiting` carry our own ids
   * and an addressing trio whose members are the PARENT's, never the row's own. The
   * thread detail is the only thing that holds both, and `<PostThread>` was already
   * handing it over through `onLoaded` with nobody listening.
   */
  let externalOf = $state<Map<string, string>>(new Map());

  function noteThread(detail: PostDetail): void {
    const m = new Map<string, string>();
    if (detail.post.external_id) m.set(`post:${detail.post.id}`, detail.post.external_id);
    for (const c of detail.comments) if (c.external_id) m.set(`comment:${c.id}`, c.external_id);
    externalOf = m;
  }

  /** Already answered ON SKOOL, by a like this browser did not make. Unknown ids are
   *  NOT liked: `likedIds` is null until the walk resolves, and a missing answer must
   *  never read as "dealt with". */
  const likedOnSkool = (r: QueueRow): boolean => {
    const ext = externalOf.get(key(r));
    return !!ext && !!likedIds?.has(ext);
  };

  /**
   * Do not stop on something the owner has already liked.
   *
   * The product rule is already written down one function up: liking something IS
   * marking it dealt with. It only ever applied to a like made HERE, so a thumb tapped
   * in Skool's mobile app on the train left the row waiting, and the deck asked for it
   * again — the same comment, already answered, with the reply box focused on it.
   *
   * ONE WRITE, NOT A LOOP. `current` derives from `pos`, so advancing one step per
   * effect pass would re-enter this on its own output; the whole run is walked first
   * and `pos` set once.
   *
   * IT DOES NOT CLEAR ANYTHING. The row keeps its place in the queue and "Done with
   * Post" still counts it — this only declines to put the cursor there. Clearing on
   * the strength of a like we merely OBSERVED is a server write nobody asked for, and
   * `likedIds` arrives seconds after the card does; a wrong one would be silent.
   *
   * Back is honoured. Stepping back onto a liked row would otherwise be thrown forward
   * again instantly, which is a trap rather than a convenience, so `back()` suspends
   * this until the reader moves forward under their own steam.
   */
  let skipLiked = $state(true);

  $effect(() => {
    if (!skipLiked) return;
    // Read both so the pass re-runs when the walk resolves, not only when `pos` moves.
    void likedIds;
    void externalOf;
    let next = pos;
    while (next < rows.length && likedOnSkool(rows[next]!)) next += 1;
    if (next !== pos) pos = next;
  });

  /**
   * ONE TILE PER WAITING POST, up to this many.
   *
   * The unit here is posts and the window is finishable, so the bar can be the actual
   * count rather than a ratio of it — tiles you claim one at a time, and a glance says
   * how many are left without reading a number. Past a dozen they stop being countable
   * and become stripes, so the wider windows fall back to a plain fill.
   */
  const TILE_MAX = 12;
  /** The count is a snapshot taken when the window opened, so answering past it is
   *  possible; the bar clamps rather than overflowing. */
  const answered = $derived(Math.min(postsDone, windowTotal ?? 0));
  const tiles = $derived(windowTotal && windowTotal <= TILE_MAX ? windowTotal : 0);
  const pct = $derived(
    windowTotal ? Math.min(100, Math.round(((answered + postFraction) / windowTotal) * 100)) : 0,
  );

  /** Where the reader is, in their terms. A ratio ("2/5 posts") is a fact about the
   *  data; this is a sentence about them, and the pills already name the window. */
  const progress = $derived<string | null>(
    !windowTotal
      ? null
      : answered >= windowTotal
        ? `All ${windowTotal} answered`
        : answered === 0
          ? `${windowTotal} ${windowTotal === 1 ? 'post' : 'posts'} waiting`
          : `${answered} of ${windowTotal} answered`,
  );
  /** The same fact for the narrow panel, where the sentence is most of the row. The
   *  full one stays on the tooltip, so nothing is lost by shortening it. */
  const progressShort = $derived<string | null>(
    windowTotal ? `${answered}/${windowTotal}` : null,
  );

  /**
   * WHAT IS WAITING IN THE WINDOWS BEHIND THIS ONE — asked only once you are caught up.
   *
   * Finishing a window is the moment the queue stops telling you anything: "All caught
   * up" is true of the last 24 hours and says nothing about the month behind it, so the
   * reader either assumes there is nothing left or goes hunting through the pills. One
   * request, at the only moment it is useful, turns that into an offer.
   *
   * `counts` answers several bounds in one scan, so every wider window costs what one
   * costs. It is NOT folded into the normal load: on the path where there is work to do
   * it would widen the scan on every single page for a number nobody is reading yet.
   */
  let widerCounts = $state<Record<string, number> | null>(null);
  /** The `skoolId|bound` this was asked for, so it is asked once per finished window. */
  let widerFor = $state<string | null>(null);

  $effect(() => {
    const view = `${skoolId}|${bound}`;
    if (!exhausted || !skoolId || widerFor === view) return;
    widerFor = view;
    const wider = WINDOWS.slice(WINDOWS.indexOf(bound) + 1);
    if (!wider.length) return;
    void (async () => {
      try {
        const page = await fetchQueue(skoolId, { last: bound }, { limit: 1, counts: [...wider] });
        widerCounts = page.counts;
      } catch {
        // Silent: this is an offer, and a queue with nothing left in it is still a
        // finished queue. Failing to enrich it must never turn into an error.
      }
    })();
  });

  /** The nearest window behind this one that actually holds something. */
  const nextUp = $derived.by(() => {
    if (!widerCounts) return null;
    for (const w of WINDOWS.slice(WINDOWS.indexOf(bound) + 1)) {
      const n = widerCounts[w] ?? 0;
      if (n > 0) return { bound: w, count: n };
    }
    return null;
  });

  /** Where the offer sends you, in words. `all` is not a window with edges, so it is
   *  described as a direction rather than a span. */
  function furtherBack(w: Bound, n: number): string {
    return w === 'all' ? `${n} more going further back` : `${n} more in ${NAMED[w]}`;
  }

  /** The same pill the inbox filters used before they were folded away, kept because
   *  it is the shape a reader already reads as "pick one of these". */
  function pillCls(on: boolean): string {
    return `rounded-full font-medium transition-colors ${compact ? 'px-2.5 py-1 text-[0.7rem]' : 'px-3 py-1 text-xs'} ${
      on
        ? 'bg-primary text-primary-content'
        : 'bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content'
    }`;
  }
</script>

<!-- A READING COLUMN, not the width of the window. The dashboard ran post bodies to
     about 150 characters a line where Skool's own column is nearer 70, and an
     over-long measure is most of why a thread "felt like too much text" — the eye
     loses the line it is on. The panel is already narrower than this cap, so the
     same component reads the same on both. -->
<div
  class="mx-auto flex w-full max-w-3xl flex-col"
  bind:this={rootEl}
  style="--sg-float-left: {anchorLeft}px; --sg-float-right: {anchorRight}px"
>
  {#if error}
    <p class="mb-2 whitespace-normal break-words text-xs text-error">{error}</p>
  {/if}

  <!-- HOW FAR BACK, AND HOW FAR THROUGH — one card, because they are one thought: the
       window you picked and what is left of it. It sits OUTSIDE the states below, since
       the emptiest screen is exactly where someone reaches for a wider window, and an
       "All caught up" with no way to ask for more is what makes a bounded queue look
       like a broken one. Same frame as the post card underneath, minus its shadow, so
       it reads as the control for that card rather than a second piece of content. -->
  {#if skoolId}
    <div
      class="mb-3 rounded-box border hair bg-base-100 {compact ? 'p-3' : 'px-4 py-3.5'}
             {stickyTop ? 'sticky z-20 shadow-sm shadow-black/[0.03]' : ''}"
      style={stickyTop ? `top:${stickyTop}` : undefined}
    >
      <!-- ONE ROW, WRAPPING. The window and how far through it are one thought, and in
           a 340px panel two stacked rows of it cost more height than the card is worth.
           The bar takes the space the pills leave and drops to its own line only when
           there genuinely is none. -->
      <div class="flex flex-wrap items-center gap-x-2 {compact ? 'gap-y-1.5' : 'gap-y-2'}">
        <div class="flex flex-wrap items-center {compact ? 'gap-1' : 'gap-1.5'}">
          {#each WINDOWS as w (w)}
            <button
              type="button"
              class={pillCls(bound === w)}
              aria-pressed={bound === w}
              onclick={() => (bound = w)}
            >{compact ? SHORT[w] : LABEL[w]}</button>
          {/each}
        </div>

        {#if windowTotal}
        <div
          class="flex min-w-0 flex-1 items-center gap-2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={windowTotal}
          aria-valuenow={answered}
          aria-valuetext={progress}
        >
          {#if tiles}
            <!-- CAPPED AT ITS NATURAL WIDTH, not stretched. Left to fill the dashboard's
                 800px column, five tiles become five 150px slabs that read as skeleton
                 loaders rather than as a count. The cap goes on the ROW, not the tiles:
                 capping the tiles alone leaves the row full-width, and its trailing
                 whitespace strands the label at the far edge. The panel is narrower than
                 the cap, so tiles shrink to fit there and it never applies.

                 AND NOTHING MARKS THE ONE IN FOCUS. That was a ring, and at zero answered
                 it was the only blue on the bar — so the first tile read as DONE, which
                 is the one thing this must never get wrong. -->
            <div
              class="flex flex-1 items-center gap-1"
              style="max-width:{tiles * 3 + (tiles - 1) * 0.25}rem"
            >
              {#each Array.from({ length: tiles }, (_, i) => i) as i (i)}
                <span
                  class="h-2.5 flex-1 overflow-hidden rounded-full transition-colors duration-500 ease-out
                         {i < answered ? 'bg-primary' : 'bg-base-content/[0.09]'}"
                >
                  <!-- The tile being worked on fills by the fraction of this post that
                       is answered, so every reply moves the bar even when a single post
                       is the whole window. -->
                  {#if i === answered && postFraction > 0}
                    <span
                      class="block h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                      style="width:{Math.round(postFraction * 100)}%"
                    ></span>
                  {/if}
                </span>
              {/each}
            </div>
          {:else}
            <!-- Too many to count, so the fill carries it. The sweep is the one animated
                 thing on this screen: a bar that never moves on a backlog this size
                 reads as broken, and this says the number is live. -->
            <div class="h-2.5 flex-1 overflow-hidden rounded-full bg-base-content/[0.09]">
              <div
                class="sheen h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
                style="width:{pct}%"
              ></div>
            </div>
          {/if}
          <span
            class="shrink-0 font-mono text-base-content/58 {compact ? 'text-[0.62rem]' : 'text-[0.7rem]'}"
            title={progress ?? undefined}
          >
            {compact ? progressShort : progress}
          </span>
        </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if loading}
    <StateBlock state="loading" {compact} />
  {:else if exhausted || !rows.length}
    <div class="rounded-box border hair bg-base-100 text-center {compact ? 'p-6' : 'p-10'}">
      <!-- The one moment in this screen worth celebrating: the queue is a thing you
           finish, and finishing it should look different from it being empty. -->
      <div class="{compact ? 'text-4xl' : 'text-5xl'} leading-none" aria-hidden="true">🎉</div>
      <h2 class="font-display mt-3 font-semibold {compact ? 'text-base' : 'text-lg'}">All caught up</h2>
      <!-- Say WHICH window is empty, or the reader reads "nothing waiting" as a claim
           about the whole backlog and never touches the pills above. -->
      <p class="mt-1.5 text-base-content/72 {compact ? 'text-xs' : 'text-sm'}">
        {bound === 'all'
          ? "Nothing's waiting on a reply. Nice."
          : `Nothing from ${NAMED[bound]} is waiting on a reply.`}
      </p>
      <!-- DONE FOR TODAY, NOT DONE FULL STOP. Without this the reader has to guess
           whether an empty window means an empty backlog, and the pills above give no
           hint which of them still holds anything. -->
      {#if nextUp}
        <button
          class="btn btn-primary {compact ? 'btn-sm' : ''} mt-4 gap-1.5"
          onclick={() => (bound = nextUp.bound)}
        >
          {furtherBack(nextUp.bound, nextUp.count)}
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
        </button>
      {/if}

      {#if emptyHint}<div class="mt-3">{@render emptyHint()}</div>{/if}
      {#if pos > 0}
        <button class="btn btn-ghost btn-sm mt-3 gap-1.5" onclick={back}>
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14 4 9l5-5" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></svg>
          Undo the last one
        </button>
      {/if}
    </div>
  {:else if current}
    <!-- The post, its whole thread, and the reply box on whatever is in focus. Keyed
         on the POST so moving down a post's waiting comments never remounts it. -->
    {#key `${skoolId}:${current.postId}`}
      <!-- ONE SURFACE FOR THE WHOLE CARD. The post and its comments dropped their own
           frames, which on the dashboard's grey canvas left them sitting on nothing —
           and a card is exactly what says where this post starts and ends. -->
      <div class="rounded-box border hair bg-base-100 shadow-sm shadow-black/[0.04] {compact ? 'px-3 py-4' : 'px-6 py-6'}">
        <PostThread
          bind:this={thread}
          {skoolId}
          {target}
          {compact}
          {now}
          {transform}
          {errorText}
          {composer}
          onLike={reportedLike}
          onLoaded={noteThread}
          {likedIds}
          {deletedIds}
          {waitingIds}
        />
      </div>
    {/key}

    <!--
      "Done with Post" — a sticky footer pill, not a fixed one.
      ────────────────────────────────────────────────────────
      It began beside the "Skool ↗" link in the post footer, which is right in a
      layout sense and wrong in every practical one: a thread with nine comments is
      several screens tall, that footer is at the TOP of it, and the moment somebody
      decides they are done with a post is the moment they have read to the bottom. So
      it floats.

      PINNED TO THE BOTTOM-RIGHT, and it stays there whatever the card is doing.

      It was briefly `sticky`, which is subtly wrong for a control that should always
      be to hand: sticky only pins once its container OVERFLOWS, so on a short card —
      most of them — it simply sat at its flow position, stranded in the whitespace
      below the post rather than in a corner. A control whose location depends on how
      long the thread happens to be is one you have to go and look for.

      RIGHT, not left, because `fixed` anchors to the VIEWPORT and this component has
      two hosts that do not share one. The Helper panel IS the viewport, but the
      dashboard puts a `lg:block` sidebar down the left — so a viewport-anchored
      `left-5` would land this on top of the community nav, and no responsive offset
      fixes that either because the sidebar collapses between `w-60` and `w-14`. The
      right edge is content in both hosts, so it is the one corner that is safe here.

      `<ReplyComposer>`'s "Back to your reply" moved to bottom-LEFT to make room. The
      two appear under overlapping conditions, and a shared corner means whichever
      paints second covers the other at side-panel width.

      GREEN, where everything else here is primary-blue or neutral: this is the one
      control on the card that ENDS something, and the one that cannot be undone in a
      press — `back()` restores a single row, so a nine-row clear has no matching step
      backwards. Colour does the same job the count does, making the weight of the
      press visible before it happens.

      The count rides in a pill rather than the label, so the label reads exactly
      "Done with Post" at a glance and the size is there for anyone who looks. Hidden
      at zero rather than disabled: nothing left to clear means the deck is about to
      advance anyway, and a greyed control that never becomes pressable is furniture.

      `pointer-events-none` on the sticky strip so the dead space beside the pill does
      not swallow clicks meant for the thread underneath it.
    -->
    {#if completable > 0}
      <div class="pointer-events-none fixed bottom-5 z-40 flex justify-end" style="right: var(--sg-float-right, 1.25rem)">
        <button
          type="button"
          class="btn btn-success btn-sm pointer-events-auto gap-1.5 rounded-full shadow-lg shadow-black/20"
          title={completeTitle}
          onclick={completePost}
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          Done with Post
          {#if completable > 1}
            <span class="rounded-full bg-black/15 px-1.5 py-0.5 text-[0.7rem] font-semibold leading-none">
              {completable}
            </span>
          {/if}
        </button>
      </div>
    {/if}
  {/if}
</div>


<style>
  /*
   * The sweep on the wide-window bar. It lives here rather than in either app's
   * `app.css` for the same reason the component does: one definition, and a keyframe
   * copied into two stylesheets is the drift this package exists to stop.
   */
  .sheen {
    position: relative;
    overflow: hidden;
  }
  .sheen::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(100deg, transparent 20%, rgb(255 255 255 / 0.5) 50%, transparent 80%);
    transform: translateX(-100%);
    animation: sheen 2.8s ease-in-out infinite;
  }
  @keyframes sheen {
    to {
      transform: translateX(100%);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .sheen::after {
      animation: none;
    }
  }
</style>
